import { CircularProgress, LinearProgress, Stack, styled, useTheme } from '@mui/material';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { FloatingStripGroup } from 'src/components/FloatingStrip/floating-strip';
import { isProgressTerminal } from 'src/graphql/entities/custom-view/custom-view-bulk-update';

export const BANNER_ICON_SIZE = 14;

const STATUS_ICON_SIZE = 16;

export const BulkJobStrip = styled(FloatingStripGroup, { name: 'BulkJobStrip' })({
  gap: '0.25rem',
  paddingLeft: '0.625rem',
  overflow: 'hidden',
  maxWidth: 'min(36rem, 100%)',
});

export const BulkJobText = styled(Stack, { name: 'BulkJobText' })(({ theme }) => ({
  flex: '1 1 auto',
  minWidth: 0,
  padding: '0.125rem 0.5rem 0.125rem 0.25rem',
  lineHeight: 'normal',
  '& > *': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  '& > b': {
    fontSize: '0.78125rem',
    fontWeight: 600,
    color: theme.palette.custom.OV.sys.onBackground,
  },
  '& > span': {
    fontSize: '0.71875rem',
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
  },
}));

export const BulkJobProgress = styled(LinearProgress, { name: 'BulkJobProgress' })(
  ({ theme }) => ({
    position: 'absolute',
    left: '0.75rem',
    right: '0.75rem',
    bottom: 0,
    height: '0.125rem',
    borderRadius: '0.125rem',
    backgroundColor: 'transparent',
    '& .MuiLinearProgress-bar': { backgroundColor: theme.palette.custom.OV.sys.secondary },
  }),
);

interface BulkJobStatusIconProps {
  status?: string;
  isUndoInProgress: boolean;
}

export const BulkJobStatusIcon = ({ status, isUndoInProgress }: BulkJobStatusIconProps) => {
  const { sys, extended } = useTheme().palette.custom.OV;

  if (isUndoInProgress || !isProgressTerminal(status)) {
    return (
      <CircularProgress
        size={BANNER_ICON_SIZE}
        thickness={5}
        aria-hidden
        sx={{ color: sys.onSurfaceVariant, flexShrink: 0 }}
      />
    );
  }

  const statusIcons: Record<string, { name: string; color: string }> = {
    failed: { name: 'circle-x', color: sys.error },
    partial_failure: { name: 'alert-circle', color: extended.Signature.color },
  };
  const { name, color } = statusIcons[status ?? ''] ?? {
    name: 'circle-check',
    color: sys.primary,
  };

  return (
    <DynamicIcon
      name={name}
      library="lucide"
      size={STATUS_ICON_SIZE}
      sx={{ color, flexShrink: 0 }}
      data-testid="bulk-update-banner-status-icon"
    />
  );
};
